import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import {
  MarketingSection,
  SectionIntro,
  SectionKicker,
  SectionTitle,
} from "../MarketingSection";
import LineIcon from "./LineIcon";

const ICON_TONES = [
  "bg-[#E6EAFF] text-[#304BE8]",
  "bg-[#FFE1EF] text-[#B81468]",
  "bg-[#DFFBF3] text-[#147763]",
  "bg-[#FFF0DB] text-[#D15300]",
  "bg-[#EEE8FF] text-[#6545C9]",
  "bg-[#DFF7FF] text-[#087A9E]",
];

type ReachSectionProps = {
  kicker: string;
  title: ReactNode;
  intro: string;
  items: { icon: ReactNode; title: string; body: string }[];
};

/** Two-column list of places or uses, each with a tinted icon. */
export default function ReachSection({
  kicker,
  title,
  intro,
  items,
}: ReachSectionProps) {
  return (
    <MarketingSection className="bg-[#F6F7FB]">
      <SectionKicker>{kicker}</SectionKicker>
      <SectionTitle className="[&_span]:text-blue-600">{title}</SectionTitle>
      <SectionIntro>{intro}</SectionIntro>
      <div className="mt-12 grid grid-cols-2 gap-x-[54px] border-t border-[#DFE3F1] max-[680px]:grid-cols-1">
        {items.map((item, i) => (
          <article
            key={item.title}
            className="grid grid-cols-[48px_1fr] gap-[18px] border-b border-[#DFE3F1] py-[27px]"
          >
            <span
              className={cn(
                "grid size-[42px] place-items-center rounded-[12px]",
                ICON_TONES[i % ICON_TONES.length],
              )}
            >
              <LineIcon className="size-[23px]">{item.icon}</LineIcon>
            </span>
            <div>
              <h3 className="m-0 mb-[7px] font-figtree text-[21px] font-[850] leading-[1.2]">
                {item.title}
              </h3>
              <p className="m-0 font-open-sans text-[16px] leading-[1.55] text-marketing-muted">
                {item.body}
              </p>
            </div>
          </article>
        ))}
      </div>
    </MarketingSection>
  );
}
